import { IBiobank } from "./biobanks.interface";
import { ICancerType } from "./cancer-types.interface";

export interface ISkippedAndDuplicates {
  skip: ISkippedInfo[];
  duplicates: string[];
}

export interface ISkippedInfo {
  sampleName: string;
  code: ISkippedErrorCode;
}

export enum ISkippedErrorCode {
  BIOBANK_NOT_EXIST = "BIOBANK_NOT_EXIST",
  CANCER_NOT_EXIST = "CANCER_NOT_EXIST",
  MISSING_SEX = "MISSING_SEX",
  MISSING_AGE = "MISSING_AGE",
  MISSING_NEOADJ_CHEMO = "MISSING_NEOADJ_CHEMO",
  MISSING_ADJ_CHEMO = "MISSING_ADJ_CHEMO",
  MISSING_AFTER_TREATMENT = "MISSING_AFTER_TREATMENT",
  MISSING_SAMPLE_TYPE = "MISSING_SAMPLE_TYPE",
  MISSING_TUMOR_LOCATION = "MISSING_TUMOR_LOCATION",
  MISSING_HISTOLOGY = "MISSING_HISTOLOGY",
  MISSING_T_STAGE = "MISSING_T_STAGE",
  MISSING_N_STAGE = "MISSING_N_STAGE",
  MISSING_M_STAGE = "MISSING_M_STAGE",
}

export interface ISampleMetadata {
  metadata: IMetadata;
  treatment: ITreatment;
  tumor: ITumor;
}

/**
 * A metadata group as sent to modify a sample: every field is null when left unchanged, the API then keeping it as is.
 */
export type IModifiedFields<T> = { [K in keyof T]: T[K] | null };

export interface ISampleMetadataModificationDetails {
  personal: {
    name: string | null;
    firstname: string | null;
    demographic: IModifiedFields<IDemographic>;
    medical: IModifiedFields<IMedical>;
    symptomatologyAfterSurgery: IModifiedFields<ISymptomatologyAfterSurgery>;
  };
  treatment: {
    afterSurgery: IModifiedFields<IAfterSurgery>;
    beforeSurgery: IModifiedFields<IBeforeSurgery>;
    description: string | null;
    surgery: string | null;
  };
  tumor: {
    diagnostic: IModifiedFields<IDiagnostic>;
    differentiationDegree: number | null;
    metastasis: IModifiedFields<IMetastasis>;
    mrnaSeqID: string | null;
    mutation: IModifiedFields<IMutation>;
    sampleType: string | null;
    sequencing: IModifiedFields<ISequencing>;
    staging: IModifiedFields<IStaging>;
    stroma: IModifiedFields<IStroma>;
    tumorCharacteristicsAndHistology: IModifiedFields<ITumorCharacteristicsAndHistology>;
  };
}

export interface IMetadata {
  biobank: IBiobank;
  cancerType: ICancerType;
  demographic: IDemographic;
  id: number;
  medical: IMedical;
  sampleID: string;
  name: string | null;
  firstname: string | null;
  symptomatologyAfterSurgery: ISymptomatologyAfterSurgery;
}

export enum Sex {
  MALE = "MALE",
  FEMALE = "FEMALE",
}

export interface IDemographic {
  age: number;
  bmi: number | null;
  sex: Sex;
  smokeTobacco: boolean | null;
}

export enum DiabetesType {
  NONE = "NONE",
  PRE_DIABETES = "PRE_DIABETES",
  TYPE1 = "TYPE1",
  TYPE2 = "TYPE2",
  UNKNOWN = "UNKNOWN",
}

export interface IMedical {
  breastCancerHist: boolean | null;
  colonPolyp: boolean | null;
  diabetes: boolean | null;
  diabetesType: DiabetesType | null;
  familialAdenomatousPolyp: boolean | null;
  inflammatoryBowelDisease: boolean | null;
  lynchSyndrome: boolean | null;
  ovarianCancerHist: boolean | null;
  rcuhCrohn: boolean | null;
  uterineCancerHist: boolean | null;
}

export interface ISymptomatologyAfterSurgery {
  abdominalCramp: boolean | null;
  ascites: boolean | null;
  bloodInStool: boolean | null;
  constipation: boolean | null;
  diarrhea: boolean | null;
  intestinalObstruction: boolean | null;
  jaundice: boolean | null;
  lossAppetite: boolean | null;
  lumpInAbdomen: boolean | null;
  rectalBleeding: boolean | null;
  rectalPain: boolean | null;
  swollenLiver: boolean | null;
  swollenLymphNodes: boolean | null;
  weaknessAndFatigue: boolean | null;
}

export interface ITreatment {
  afterSurgery: IAfterSurgery;
  beforeSurgery: IBeforeSurgery;
  description: string | null;
  surgery: string | null;
}

export interface IAfterSurgery {
  adjuvantChemotherapy: string;
  combChemo: string | null;
  immunotherapy: string | null;
  radio: boolean | null;
}

export interface IBeforeSurgery {
  combChemo: string | null;
  immunotherapy: string | null;
  neoChemo: boolean;
  radio: boolean;
}

export interface ITumor {
  diagnostic: IDiagnostic;
  differentiationDegree: number | null;
  id: number;
  metastasis: IMetastasis;
  mrnaSeqID: string | null;
  mutation: IMutation;
  sampleType: string;
  sequencing: ISequencing;
  staging: IStaging;
  stroma: IStroma;
  tumorCharacteristicsAndHistology: ITumorCharacteristicsAndHistology;
}

export interface IDiagnostic {
  biopsy: boolean | null;
  coloCt: boolean | null;
  colonoscopy: boolean | null;
  rectalExam: boolean | null;
}

export interface IMetastasis {
  adrenal: boolean | null;
  bone: boolean | null;
  brain: boolean | null;
  breast: boolean | null;
  kidney: boolean | null;
  liver: boolean | null;
  lung: boolean | null;
  lymphNode: boolean | null;
  pancreas: boolean | null;
  peritoneum: boolean | null;
}

export interface IMutation {
  apc: boolean | null;
  braf: boolean | null;
  kras: boolean | null;
  mlh1: boolean | null;
  mmr: boolean | null;
  msiStatus: string | null;
  nras: boolean | null;
  pms2: boolean | null;
}

export interface ISequencing {
  sequencingNgs: boolean | null;
}

export interface IStaging {
  pathologicalStage: string | null;
  ptnmM: string;
  ptnmN: string;
  ptnmT: string;
}

export interface IStroma {
  inflammatoryStroma: boolean | null;
}

export interface ITumorCharacteristicsAndHistology {
  histology: string;
  histologyType: string | null;
  carcino: boolean | null;
  gist: boolean | null;
  gistType: string | null;
  location: string;
  lymphoma: boolean | null;
  lymphomaType: string | null;
}

export interface IMetadataOptions {
  sex: Sex[];
  location: string[];
  histology: string[];
  ptnmT: string[];
  ptnmN: string[];
  ptnmM: string[];
  pathologicalStage: string[];
  msiStatus: string[];
  radiotherapy: string[];
  neoadjuvant: string[];
}
